import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useQuery, type UseQueryResult} from '@tanstack/react-query'

import Toast from 'components/Basic/Toast'
import {
  AIUsageAppUsersAPIResponseData,
  AIUsageAppUsersAPIResponse,
} from 'interfaces'
import {AIUsageService} from 'services/api'

export const AI_USAGE_APP_USERS_QUERY_KEY = 'getAIUsageAppUsers'

export type UseAIUsageAppUsersQueryProps = {
  appName: string
  enabled?: boolean
  /**
   * When true (default), shows the generic fetch error toast on failure.
   * Set false to handle errors in the caller (e.g. custom UI or messaging).
   */
  showErrorToast?: boolean
}

type UseAIUsageAppUsersQueryResult = Pick<
  UseQueryResult<AIUsageAppUsersAPIResponseData, Error>,
  'isLoading' | 'isSuccess' | 'isError' | 'error' | 'refetch'
> & {
  data?: AIUsageAppUsersAPIResponse
}

export const useAIUsageAppUsersQuery = ({
  appName,
  enabled = true,
  showErrorToast = true,
}: UseAIUsageAppUsersQueryProps): UseAIUsageAppUsersQueryResult => {
  const {t} = useTranslation(['aiUsage'])
  const {data, isLoading, isSuccess, isError, error, refetch} = useQuery<
    AIUsageAppUsersAPIResponseData,
    Error
  >({
    queryKey: [AI_USAGE_APP_USERS_QUERY_KEY, appName],
    queryFn: () => {
      if (!appName) {
        return Promise.resolve({data: {users: []}})
      }
      return AIUsageService.getAIUsageAppUsers(appName)
    },
    enabled: Boolean(enabled && appName),
  })

  useEffect(() => {
    if (isError && showErrorToast) {
      Toast({
        message: t('aiUsage:AI_USAGE_TOAST_APP_USERS_FETCH_ERROR'),
        type: 'error',
        customId: 'ai-usage-app-users-fetch-error',
      })
    }
  }, [isError, showErrorToast, t])

  return {
    data: data?.data,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  }
}
